/* ==========================================================================
   Filtering and sorting

   One <form> holds the sort select, every filter input and the mobile drawer.
   Any change serialises the form, pushes the resulting query string to history
   and swaps in a freshly rendered section — so filtered URLs stay shareable and
   the back button works.
   ========================================================================== */

class BpiFacets extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('#BpiFacetForm');
    if (!this.form) return;

    this.sectionId = this.dataset.sectionId;
    this.baseUrl = this.dataset.baseUrl || window.location.pathname;
    this.controller = null;
    this.debounceTimer = null;

    this.form.addEventListener('change', (e) => this.onChange(e));
    this.form.addEventListener('submit', (e) => e.preventDefault());
    this.addEventListener('click', (e) => this.onClick(e));

    // Restore state when the user navigates back to a previous filter set
    this.onPopState = () => this.render(window.location.search, false);
    window.addEventListener('popstate', this.onPopState);

    this.setupResponsiveRail();
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopState);
    this.mq && this.mq.removeEventListener('change', this.placeRail);
  }

  /* ---------- The rail lives in the aside on desktop, the drawer on mobile ---------- */

  setupResponsiveRail() {
    this.railSlot = this.querySelector('#BpiRailSlot');
    this.drawerSlot = this.querySelector('#BpiDrawerSlot');
    if (!this.railSlot || !this.drawerSlot) return;

    this.mq = window.matchMedia('(min-width: 990px)');
    this.placeRail = () => {
      const rail = this.querySelector('#BpiFacetRail');
      if (!rail) return;
      const target = this.mq.matches ? this.railSlot : this.drawerSlot;
      if (rail.parentElement !== target) target.appendChild(rail);
    };
    this.mq.addEventListener('change', this.placeRail);
    this.placeRail();
  }

  /* ---------- Events ---------- */

  onChange(event) {
    const isText = event.target.matches('input[type="number"], input[type="text"]');
    // Price fields fire on every keystroke — let the user finish typing
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.submit(), isText ? 500 : 0);
  }

  onClick(event) {
    const removeLink = event.target.closest('.bpi-chip, [data-facet-clear-all]');
    if (!removeLink) return;
    event.preventDefault();
    const url = new URL(removeLink.href, window.location.origin);
    this.render(url.search, true);
  }

  /* ---------- Serialise → fetch → swap ---------- */

  submit() {
    const data = new FormData(this.form);
    const params = new URLSearchParams();

    for (const [key, value] of data.entries()) {
      // Empty price fields must not become filter.v.price.gte=
      if (value === '' || value == null) continue;
      params.append(key, value);
    }

    this.render(`?${params.toString()}`, true);
  }

  async render(search, push) {
    const query = search && search !== '?' ? search : '';
    const url = `${this.baseUrl}${query}`;
    const fetchUrl = `${this.baseUrl}${query ? query + '&' : '?'}section_id=${this.sectionId}`;

    this.setLoading(true);

    if (this.controller) this.controller.abort();
    this.controller = new AbortController();

    try {
      const response = await fetch(fetchUrl, { signal: this.controller.signal });
      if (!response.ok) throw new Error(`Filter request failed: ${response.status}`);
      const text = await response.text();
      const parsed = new DOMParser().parseFromString(text, 'text/html');

      this.swap(parsed, '#BpiProductGrid');
      this.swap(parsed, '#BpiFacetRail');
      this.swap(parsed, '#BpiProductCount');

      // The sort select is outside the swapped regions — keep it in step
      const newSort = parsed.querySelector('#BpiSortBy');
      const sort = this.querySelector('#BpiSortBy');
      if (newSort && sort) sort.value = newSort.value;

      this.placeRail && this.placeRail();

      if (push) window.history.pushState({ search: query }, '', url);

      this.announce(parsed);
      this.scrollIntoViewIfNeeded();
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error(error);
      // Fall back to a full page load rather than leaving a stale grid
      window.location.href = url;
    } finally {
      this.setLoading(false);
    }
  }

  swap(parsed, selector) {
    const incoming = parsed.querySelector(selector);
    const current = this.querySelector(selector);
    if (incoming && current) current.innerHTML = incoming.innerHTML;
  }

  setLoading(state) {
    const spinner = this.querySelector('[data-facet-loading]');
    if (spinner) spinner.hidden = !state;
    const grid = this.querySelector('#BpiProductGrid');
    if (grid) grid.toggleAttribute('data-loading', state);
  }

  announce(parsed) {
    const count = parsed.querySelector('#BpiProductCount');
    const live = this.querySelector('#BpiProductCount');
    if (count && live) live.setAttribute('role', 'status');
  }

  scrollIntoViewIfNeeded() {
    const top = this.getBoundingClientRect().top;
    if (top < 0) window.scrollTo({ top: window.scrollY + top - 20, behavior: 'smooth' });
  }
}

customElements.define('bpi-facets', BpiFacets);
